'use client'

import { useIntl } from 'react-intl'
import { Dialog, Cell, Text, Button, Divider, Navigation } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  formatCurrencyAmount,
  ReturnCurrencyConversionModel,
  ReturnShippingMethod,
  ReturnShippingOption,
} from '@marketplace-web/complaints/complaints-data'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'

type Props = {
  isOpen: boolean
  conversion: ReturnCurrencyConversionModel | null | undefined
  selectedShippingOption: ReturnShippingOption | undefined
  onClose: () => void
}

const ReturnCurrencyConversionModal = ({
  isOpen,
  conversion,
  selectedShippingOption,
  onClose,
}: Props) => {
  const { formatNumber, locale } = useIntl()
  const translate = useTranslate(
    'conversation.actions.return_shipping_options.currency_conversion_modal',
  )

  function renderNavigation() {
    return (
      <Navigation
        body={translate('title')}
        right={
          <Button
            inline
            iconName={X24}
            styling={Button.Styling.Flat}
            testId="close-return-currency-conversion-modal"
            onClick={onClose}
          />
        }
      />
    )
  }

  function renderItemPriceConversionInfo() {
    if (!conversion?.itemPrice) return null

    const { beforeConversionPrice, afterConversionPrice } = conversion.itemPrice

    const priceBeforeConversion = formatCurrencyAmount(beforeConversionPrice, locale)
    const priceAfterConversion = formatCurrencyAmount(afterConversionPrice, locale)

    return (
      <Cell
        title={translate('price')}
        body={
          <Text
            as="h3"
            type={Text.Type.Subtitle}
            html
            text={translate('price_description', { cost: priceBeforeConversion })}
          />
        }
        suffix={priceAfterConversion}
      />
    )
  }

  function renderShippingPriceConversionInfo() {
    if (!conversion?.factualShipping) return null

    const { beforeConversionPrice, afterConversionPrice, method } = conversion.factualShipping

    const priceBeforeConversion = formatCurrencyAmount(beforeConversionPrice, locale)
    const priceAfterConversion = formatCurrencyAmount(afterConversionPrice, locale)
    const isIntegratedShipping = method === ReturnShippingMethod.Integrated

    if (parseFloat(afterConversionPrice.amount) === 0) {
      return (
        <Cell
          title={translate('shipping')}
          body={
            <Text
              as="h3"
              type={Text.Type.Subtitle}
              text={
                isIntegratedShipping
                  ? translate('shipping_description.covered_by_vinted')
                  : translate('shipping_description.covered_by_seller')
              }
            />
          }
          suffix={priceAfterConversion}
        />
      )
    }

    return (
      <Cell
        title={translate('shipping')}
        body={
          <Text
            as="h3"
            html
            type={Text.Type.Subtitle}
            text={translate('shipping_description.converted', { cost: priceBeforeConversion })}
          />
        }
        suffix={priceAfterConversion}
      />
    )
  }

  function renderBuyerProtectionFeeInfo() {
    if (!conversion?.buyerProtectionFee) return null

    const price = formatCurrencyAmount(conversion.buyerProtectionFee, locale)

    return <Cell title={translate('buyer_protection_fee')} suffix={price} />
  }

  const renderAuthenticityCheckFee = () => {
    if (!conversion?.authenticityCheckFee) return null

    const price = formatCurrencyAmount(conversion.authenticityCheckFee, locale)

    return <Cell title={translate('authenticity_check_fee_refund')} suffix={price} />
  }

  const renderElectronicsVerificationFee = () => {
    if (!conversion?.electronicsVerificationFee) return null

    const price = formatCurrencyAmount(conversion.electronicsVerificationFee, locale)

    return <Cell title={translate('electronics_check_fee_refund')} suffix={price} />
  }

  function renderReturnShippingInfo() {
    if (selectedShippingOption === ReturnShippingOption.Custom) return null
    if (!conversion?.returnShipping) return null

    const { beforeConversionPrice, afterConversionPrice } = conversion.returnShipping

    const priceBeforeConversion = formatCurrencyAmount(beforeConversionPrice, locale)
    const priceAfterConversion = formatCurrencyAmount(afterConversionPrice, locale)

    if (parseFloat(afterConversionPrice.amount) === 0) {
      return (
        <Cell
          title={translate('return_shipping')}
          body={
            <Text
              as="h3"
              type={Text.Type.Subtitle}
              text={translate('shipping_description.covered_by_vinted')}
            />
          }
          suffix={priceAfterConversion}
        />
      )
    }

    return (
      <Cell
        title={translate('return_shipping')}
        body={
          <Text
            as="h3"
            html
            type={Text.Type.Subtitle}
            text={translate('return_shipping_description', { cost: priceBeforeConversion })}
          />
        }
        suffix={priceAfterConversion}
      />
    )
  }

  function renderExchangeRate() {
    if (!conversion) return null

    const { baseRate, roundedRate } = conversion.exchange

    const base = formatCurrencyAmount(baseRate, locale)
    const rate = formatCurrencyAmount(roundedRate, locale)

    return (
      <>
        <Divider />
        <Cell>
          <Text
            as="span"
            text={`${translate('fx_rate.title')} ${translate('fx_rate.rate', { rate, base })}`}
          />
        </Cell>
      </>
    )
  }

  function renderPaymentServiceProviderFee() {
    if (!conversion?.markup) return null

    const { rate, provider } = conversion.markup

    const feePercentage = formatNumber(parseFloat(rate), {
      style: 'percent',
      maximumFractionDigits: 2,
    })

    return (
      <Cell
        body={
          <Text
            as="h3"
            html
            type={Text.Type.Subtitle}
            text={translate('fx_markup_rate', { fee: feePercentage, payment_provider: provider })}
          />
        }
      />
    )
  }

  function renderActions() {
    return (
      <Cell>
        <Button text={translate('actions.ok')} styling={Button.Styling.Filled} onClick={onClose} />
      </Cell>
    )
  }

  return (
    <Dialog show={isOpen}>
      <SeparatedList separator={<Divider />}>
        {renderNavigation()}
        {renderItemPriceConversionInfo()}
        {renderShippingPriceConversionInfo()}
        {renderBuyerProtectionFeeInfo()}
        {renderAuthenticityCheckFee()}
        {renderElectronicsVerificationFee()}
        {renderReturnShippingInfo()}
      </SeparatedList>
      {renderExchangeRate()}
      {renderPaymentServiceProviderFee()}
      {renderActions()}
    </Dialog>
  )
}

export default ReturnCurrencyConversionModal
